import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

import { authApi } from 'Api';

let listeners: (() => void)[] = [];
let connectionHealthInterval: number | null = null;
let isConnectionInitialized = false;
let connectionRetryAttempts = 0;
const MAX_RETRY_ATTEMPTS = 5;

const getLogLevel = (): LogLevel => {
  if (import.meta.env.DEV) {
    return LogLevel.Debug;
  }

  return LogLevel.Warning;
};

const getSignalRUrl = (): string => {
  if (import.meta.env.DEV) {
    return 'http://localhost:5000/api/coursesHub';
  }

  return `${window.location.origin}/api/coursesHub`;
};

export const signalRConnection = new HubConnectionBuilder()
  .withUrl(getSignalRUrl(), {
    withCredentials: true,
    timeout: 300000,
    skipNegotiation: false
  })
  .configureLogging(getLogLevel())
  .withAutomaticReconnect({
    nextRetryDelayInMilliseconds: retryContext => {
      if (retryContext.previousRetryCount === 0) {
        return 1000;
      } else if (retryContext.previousRetryCount === 1) {
        return 5000;
      } else if (retryContext.previousRetryCount === 2) {
        return 15000;
      } else if (retryContext.previousRetryCount === 3) {
        return 30000;
      } else {
        return 60000;
      }
    }
  })
  .build();

const handleSessionExpired = async () => {
  console.warn('SignalR session expired, handling cleanup and re-authentication');

  if (connectionHealthInterval) {
    window.clearInterval(connectionHealthInterval);
    connectionHealthInterval = null;
  }

  try {
    await signalRConnection.stop();
    emitStatusChange();
    signalRConnection.off('session_expired');

    const result = await authApi.getCookie();
    if (result.isSuccess && result.data?.isAuthenticated === true) {
      console.log('User still authenticated, reconnecting SignalR...');
      connectSignalR();
    } else {
      console.log('User not authenticated, triggering logout...');
      await handleLogout();
    }
  } catch (error) {
    console.error('Error handling session expiry:', error);
    await handleLogout();
  }
};

const handleLogout = async () => {
  try {
    const { logout } = await import('Auth/data');
    const { store } = await import('Store/store');
    store.dispatch(logout());
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

const startConnectionHealthMonitoring = () => {
  if (connectionHealthInterval) {
    window.clearInterval(connectionHealthInterval);
  }

  connectionHealthInterval = window.setInterval(() => {
    if (signalRConnection.state === HubConnectionState.Disconnected) {
      console.warn('SignalR connection lost, attempting to reconnect...');
      connectSignalR();
    }
  }, 30000);
};

const checkAuthentication = async (): Promise<boolean> => {
  try {
    const result = await authApi.getCookie();
    return result.isSuccess && result.data?.isAuthenticated === true;
  } catch (error) {
    console.error('Failed to check authentication status:', error);
    return false;
  }
};

export const connectSignalR = async () => {
  if (signalRConnection.state === HubConnectionState.Connecting) {
    console.log('SignalR connection already in progress');
    return;
  }

  if (signalRConnection.state === HubConnectionState.Connected) {
    console.log('SignalR already connected');
    return;
  }

  const isAuthenticated = await checkAuthentication();
  if (!isAuthenticated) {
    console.log('User not authenticated, skipping SignalR connection');
    return;
  }

  if (connectionRetryAttempts >= MAX_RETRY_ATTEMPTS) {
    console.error(`SignalR connection failed after ${MAX_RETRY_ATTEMPTS} attempts, giving up`);
    return;
  }

  try {
    console.log(`SignalR connection attempt ${connectionRetryAttempts + 1}/${MAX_RETRY_ATTEMPTS}`);

    await signalRConnection.start();

    console.log('SignalR connected successfully');
    connectionRetryAttempts = 0;
    isConnectionInitialized = true;
    emitStatusChange();

    signalRConnection.on('session_expired', handleSessionExpired);

    signalRConnection.onreconnecting(() => {
      console.log('SignalR reconnecting...');
      emitStatusChange();
    });

    signalRConnection.onreconnected(() => {
      console.log('SignalR reconnected successfully');
      emitStatusChange();
    });

    signalRConnection.onclose(error => {
      emitStatusChange();

      if (error) {
        console.warn('SignalR connection closed with error:', {
          error: error.message || error,
          errorType: error.name || 'UnknownError',
          connectionState: signalRConnection.state,
          timestamp: new Date().toISOString(),
          retryAttempts: connectionRetryAttempts
        });

        if (
          !error.message?.includes('Connection closed by the server') &&
          !error.message?.includes('WebSocket closed with status code: 1000')
        ) {
          console.error('SignalR unexpected disconnection:', error);
        }
      } else {
        console.log('SignalR connection closed gracefully');
      }
    });

    startConnectionHealthMonitoring();
  } catch (error) {
    connectionRetryAttempts++;
    console.error(`SignalR connection failed (attempt ${connectionRetryAttempts}):`, error);

    if (connectionRetryAttempts < MAX_RETRY_ATTEMPTS) {
      const retryDelay = Math.min(5000 * connectionRetryAttempts, 30000);
      window.setTimeout(() => {
        connectSignalR();
      }, retryDelay);
    }
  }
};

export const disconnectSignalR = () => {
  console.log('Disconnecting SignalR...');

  if (connectionHealthInterval) {
    window.clearInterval(connectionHealthInterval);
    connectionHealthInterval = null;
  }

  signalRConnection.off('session_expired');
  signalRConnection.onreconnecting(() => {});
  signalRConnection.onreconnected(() => {});
  signalRConnection.onclose(() => {});

  signalRConnection.stop().then(emitStatusChange).catch(console.error);

  isConnectionInitialized = false;
  connectionRetryAttempts = 0;
};

export function subscribeStateChange(listener: () => void) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

export const getStateSnapshot = () => {
  return signalRConnection.state;
};

export const isSignalRInitialized = () => {
  return isConnectionInitialized;
};

const emitStatusChange = () => {
  for (const listener of listeners) {
    listener();
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    disconnectSignalR();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (connectionHealthInterval) {
        window.clearInterval(connectionHealthInterval);
        connectionHealthInterval = null;
      }
    } else {
      if (signalRConnection.state === HubConnectionState.Connected) {
        startConnectionHealthMonitoring();
      }
    }
  });
}
